import { baseApi } from '@/store/api/baseApi';
import type {
  BlogListParams,
  BlogPost,
  CreateBlogPayload,
  PaginatedBlogs,
  UpdateBlogPayload,
} from '@/types/blog';

function buildBlogListQuery(params: BlogListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'updatedAt');
  searchParams.set('sortOrder', params.sortOrder ?? 'desc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);

  return searchParams.toString();
}

export const blogsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBlog: builder.query<BlogPost, string>({
      query: (id) => `/blogs/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Blogs', id }],
    }),
    getBlogs: builder.query<PaginatedBlogs, BlogListParams>({
      query: (params) => ({
        url: `/blogs?${buildBlogListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildBlogListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildBlogListQuery(currentArg ?? {}) !== buildBlogListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Blogs' as const, id })),
              { type: 'Blogs', id: 'LIST' },
            ]
          : [{ type: 'Blogs', id: 'LIST' }],
    }),
    createBlog: builder.mutation<BlogPost, CreateBlogPayload>({
      query: (body) => ({ url: '/blogs/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Blogs', id: 'LIST' }],
    }),
    updateBlog: builder.mutation<BlogPost, { id: string; body: UpdateBlogPayload }>({
      query: ({ id, body }) => ({ url: `/blogs/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Blogs', id },
        { type: 'Blogs', id: 'LIST' },
      ],
    }),
    deleteBlog: builder.mutation<void, string>({
      query: (id) => ({ url: `/blogs/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Blogs', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetBlogQuery,
  useGetBlogsQuery,
  useCreateBlogMutation,
  useUpdateBlogMutation,
  useDeleteBlogMutation,
} = blogsApi;
